import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { COMPANY_NAME } from '../config/siteInfo';
import { scrollToSectionId } from '../lib/scrollNav';
import logo from '../assets/logo.png';

const links = ['Home', 'Services', 'About', 'Projects', 'Process', 'Contact'];

function sectionHref(link) {
  return link === 'Home' ? '/' : `/#${link.toLowerCase()}`;
}

export default function Navbar({ siteName = COMPANY_NAME }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
  }, [menuOpen]);

  const runAfterMenu = (fn) => {
    const delay = menuOpen ? 280 : 0;
    setMenuOpen(false);
    setTimeout(fn, delay);
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    runAfterMenu(() => {
      if (location.pathname === '/') scrollToSectionId('home');
      else navigate('/');
    });
  };

  const handleSectionNav = (e, link) => {
    e.preventDefault();
    runAfterMenu(() => {
      if (link === 'Home') {
        if (location.pathname === '/') scrollToSectionId('home');
        else navigate('/');
        return;
      }
      const id = link.toLowerCase();
      if (location.pathname === '/') scrollToSectionId(id);
      else navigate(`/#${id}`);
    });
  };

  const handleQuote = (e) => {
    e.preventDefault();
    runAfterMenu(() => {
      if (location.pathname === '/') scrollToSectionId('contact');
      else navigate('/#contact');
    });
  };

  return (
    <>
      <nav className={`navbar${scrolled ? ' scrolled' : ''}`} aria-label="Primary">
        <div className="container">
          <div className="navbar-inner">
            <Link
              to="/"
              className="navbar-brand-link"
              onClick={handleLogoClick}
              aria-label={`${siteName} — Home`}
            >
              <img src={logo} alt="Dragon Metalworks Logo" className="navbar-logo" />
            </Link>

            <ul className="navbar-links">
              {links.map((link) => (
                <li key={link}>
                  <Link to={sectionHref(link)} onClick={(e) => handleSectionNav(e, link)}>
                    {link}
                  </Link>
                </li>
              ))}
            </ul>

            <button type="button" className="navbar-cta" onClick={handleQuote}>
              Get a Quote
            </button>

            <button
              type="button"
              className={`navbar-hamburger${menuOpen ? ' open' : ''}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-expanded={menuOpen}
              aria-label="Toggle menu"
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.22 }}
          >
            {links.map((link, i) => (
              <motion.div
                key={link}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Link to={sectionHref(link)} onClick={(e) => handleSectionNav(e, link)}>
                  {link}
                </Link>
              </motion.div>
            ))}
            <button type="button" className="mobile-cta" onClick={handleQuote}>
              Get a Quote
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
